import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { AIGenerator } from "PFApp/components/ai_generator";
import {
  FIELD_ASSIGNEE_ID_KEY,
  FIELD_BOOKING_CATEGORY_ID_KEY,
  FIELD_DESCRIPTION_KEY,
  FIELD_MIN_REQUIRED_COUNT_KEY,
  FIELD_NAME_KEY,
  FIELD_PARENT_ACTIVITY_KEY,
  FIELD_SKILLS_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { useFormValidation } from "PFApp/hooks/activity_form/use_form_validation";
import { Sections } from "PFApp/types/activity_form/activity_form_schema";
import { Inline } from "PFComponents/containers";
import { InputFieldSet } from "PFComponents/text/input_field_set";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { generateRoleDescription } from "PFCore/services/ai";
import { FeatureFlag, PermissionRule } from "PFCore/types";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useActivityFormStore } from "../../../activity_form_store";
import { useSectionsUtils } from "../../../hooks/use_sections_utils";
import { useSubtemplate } from "../../../hooks/use_subtemplate";
import { FormSection, SectionProps } from "../../form_section";
import css from "./basic_information_form_section.module.scss";
import { AssigneeSelect } from "./parts/assignee_select";
import { BookingCategory } from "./parts/booking_category";
import { EngagementSelector } from "./parts/engagement_selector";
import { ExpiryDate } from "./parts/expiry_date";
import { GeneratorDataPreview } from "./parts/generator_data_preview";
import { SegmentSection } from "./parts/segment_section";

export const BasicInformationFormSection = ({ id, title }: SectionProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form" });
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext();
  const isPermittedTo = useIsCurrentUserPermittedTo();
  const isFeatureEnabled = useIsFeatureEnabled();
  const qaIdPrefix = useActivityFormStore((state) => state.qaIdPrefix);
  const { isRole } = useActivityFormStore((state) => state.activityStatus);
  const { isFieldVisible, getSectionFieldsBy } = useSectionsUtils();
  const { draft: submittedAsDraft } = useActivityFormStore((state) => state.submitOptions);
  const template = useActivityFormStore((state) => state.template);
  const subtemplate = useSubtemplate();
  const { isFieldRequired } = useFormValidation({ subtemplate, template });

  const showActivityAiGenerator =
    isFeatureEnabled(FeatureFlag.ActivityAiGenerator) && isPermittedTo(PermissionRule.AIGeneratedDescription);

  const [name, description, skills = []] = useWatch({
    name: [FIELD_NAME_KEY, FIELD_DESCRIPTION_KEY, FIELD_SKILLS_KEY]
  });

  const handleDescriptionGenerate = ({ regenerate, longer, selectedGeneratedData }) => {
    const roleDescription = description || selectedGeneratedData?.roleDescription;
    const payload = {
      roleTitle: name,
      selectedSkills: skills.map(({ text }) => text),
      ...((regenerate || longer) && roleDescription ? { roleDescription } : {}),
      regenerate,
      longer
    };
    return generateRoleDescription(payload);
  };

  const handleDescriptionInsert = ({ roleDescription }) => {
    setValue(FIELD_DESCRIPTION_KEY, roleDescription, { shouldDirty: true });
  };

  const hasSegmentFields = getSectionFieldsBy(Sections.BasicInformation, "segment").length > 0;
  const nameRequired = isFieldRequired({ name: FIELD_NAME_KEY });
  const descriptionRequired = isFieldRequired({ name: FIELD_DESCRIPTION_KEY });
  const minRequiredCountRequired = isFieldRequired({ name: FIELD_MIN_REQUIRED_COUNT_KEY });

  return (
    <FormSection id={id} title={title} qaId="ActivityForm.basicInformation">
      <Inline>
        <Controller
          name={FIELD_NAME_KEY}
          control={control}
          rules={{
            required: nameRequired ? t("errors.cantBeBlank") : false
          }}
          render={({ field }) => (
            <InputFieldSet
              required={nameRequired}
              label={t("sections.basicInformation.name")}
              lockedTip={t("sections.basicInformation.name")}
              title={field.name}
              value={field.value}
              maxLength={100}
              qaId={`${qaIdPrefix}-title`}
              error={get(errors, field.name)?.message as string}
              onChange={field.onChange}
              className={css.name}
            />
          )}
        />
        {isFieldVisible(FIELD_MIN_REQUIRED_COUNT_KEY) && (
          <Controller
            name={FIELD_MIN_REQUIRED_COUNT_KEY}
            control={control}
            rules={{
              validate: (value: string) => {
                if (!submittedAsDraft && minRequiredCountRequired && isNil(value)) {
                  return t("errors.cantBeBlank");
                }
                return true;
              }
            }}
            render={({ field }) => (
              <InputFieldSet
                required={minRequiredCountRequired}
                label={t("sections.basicInformation.numberOfVacancies")}
                value={field.value}
                inputType="number"
                min={1}
                qaId={`${qaIdPrefix}-min-required-count`}
                error={get(errors, field.name)?.message as string}
                onChange={(minRequired) =>
                  field.onChange(isEmpty(minRequired) ? null : parseInt(minRequired))
                }
                className={css.vacancies}
              />
            )}
          />
        )}
      </Inline>
      {isFieldVisible(FIELD_PARENT_ACTIVITY_KEY) && <EngagementSelector />}
      <Controller
        name={FIELD_DESCRIPTION_KEY}
        control={control}
        rules={{
          required: descriptionRequired ? t("errors.cantBeBlank") : false
        }}
        render={({ field }) => (
          <InputFieldSet
            required={descriptionRequired}
            label={t("sections.basicInformation.description")}
            value={field.value}
            inputType="textarea"
            maxLength={25000}
            minHeight={100}
            minVisibleMaxLengthFactor={0.9}
            qaId={`${qaIdPrefix}-task-description` /* -task- avoids collision with lesson's -description */}
            error={get(errors, field.name)?.message as string}
            onChange={field.onChange}
          />
        )}
      />
      {isRole && showActivityAiGenerator && (
        <AIGenerator
          message={t("sections.basicInformation.descriptionGenerate.weAreAbleToGenerateDescription")}
          buttonText={t("sections.basicInformation.descriptionGenerate.generateDescription")}
          errorMessage={t("sections.basicInformation.descriptionGenerate.error")}
          enabled={!!name}
          onGenerate={handleDescriptionGenerate}
          onInsert={handleDescriptionInsert}
          PreviewComponent={GeneratorDataPreview}
        />
      )}
      <ExpiryDate />
      {isFieldVisible(FIELD_ASSIGNEE_ID_KEY) && <AssigneeSelect />}
      {isFieldVisible(FIELD_BOOKING_CATEGORY_ID_KEY) && <BookingCategory />}
      {hasSegmentFields && <SegmentSection />}
    </FormSection>
  );
};
